import rpcShared from '@rockpapercoin/rpc-shared';
import { useSnackbar } from 'notistack';
import React, { useCallback } from 'react';

/**
 * If a component is passed in with the error object, this method will return that component.
 * Otherwise, it will check to see if the received error is a Validation Error. If it is, it
 * will slice "Validation Error:" off of the error message and return the new string. If there
 * is no received error, it will fall back to the default error.
 */
const getDisplayNode = ( error: unknown ) => {
	if ( React.isValidElement( error ) ) {
		return error;
	}
	const validationText = 'Validation Error: ';

	if ( error === undefined || error === null || error === '' ) {
		return rpcShared.strings.errorMessages.default;
	}

	// for errors like Error or GraphQLError
	if (
		typeof error === 'object' &&
		error &&
		'message' in error &&
		error.message &&
		typeof error.message === 'string'
	) {
		return error.message.replace( validationText, '' );
	}

	if ( typeof error === 'string' ) {
		return error.replace( validationText, '' );
	}

	return rpcShared.strings.errorMessages.default;
};

export const useToast = () => {
	const { enqueueSnackbar } = useSnackbar();

	const showError = useCallback(
		( error?: unknown, autoHideDuration = 7000 ) => {
			if (
				error &&
				error instanceof Error &&
				error.message === 'Socket closed'
			) {
				// ignore websocket closed errors
			} else {
				const message = getDisplayNode( error );
				enqueueSnackbar( { variant: 'error', message, autoHideDuration } );
			}
		},
		[ enqueueSnackbar ]
	);

	const showSuccess = useCallback(
		( content = 'Success!', autoHideDuration = 3000 ) => {
			const message = getDisplayNode( content );
			enqueueSnackbar( { variant: 'success', message, autoHideDuration } );
		},
		[ enqueueSnackbar ]
	);

	return {
		showError,
		showSuccess,
	};
};
